export enum CONDITION_BEHAVIOR {
    DID = 'did',
    DIDNOT = 'didnot',
}

export enum CONDITION_ACTION {
    JOIN = 'join',
    ABANDON = 'abandon',
    PURCHASE = 'purchase',
    SPEND = 'spend',
    SEE = 'see',
    CLICK = 'click',
    CONVERT_FROM = 'convertFrom',
    IGNORE = 'ignore',
    LOCATED_AT = 'locatedAt'
}

export enum CONDITION_ANY_SPECIFIC {
    ANY = 'any',
    SPECIFIC = 'specific',
}

export enum CONDITION_AMOUNT_TYPE {
    ANY = 'any',
    EXACTLY = 'exactly',
    OVER = 'over',
    BELOW = 'below',
}

export enum CONDITION_MESSAGE {
    ANY = 'any',
    RECOVERY = 'recovery',
    EVENT = 'event',
    CAMPAIGN = 'campaign',
}

export enum CONDITION_TIMEFRAME {
    ANY = 'any',
    LAST = 'last',
    BETWEEN = 'between',
}

export enum CONDITION_TIME_UNIT {
    DAY = 'DAY',
    WEEK = 'WEEK',
    MONTH = 'MONTH',
    YEAR = 'YEAR',
}

export enum CONDITION_PRICE_TOTAL {
    TOTAL = 'total',
    EACH = 'each',
}

export interface ConditionsProps {
    behavior?: CONDITION_BEHAVIOR;
    action?: CONDITION_ACTION;
    product?: CONDITION_ANY_SPECIFIC;
    amountType?: CONDITION_AMOUNT_TYPE;
    amount?: number | string;
    campaign?: CONDITION_ANY_SPECIFIC;
    message?: CONDITION_MESSAGE;
    timeframe?: CONDITION_TIMEFRAME;
    sometimeLast?: number;
    sometimeBetween?: {
        from: Date,
        to: Date,
    };
    timeUnit?: CONDITION_TIME_UNIT;
    priceTotal?: CONDITION_PRICE_TOTAL;
    priceType?: CONDITION_AMOUNT_TYPE;
    price?: number | string;
    country?: string;
}

export const defaultConditions: ConditionsProps = {
    behavior: CONDITION_BEHAVIOR.DID,
    product: CONDITION_ANY_SPECIFIC.ANY,
    amountType: CONDITION_AMOUNT_TYPE.ANY,
    amount: 1,
    campaign: CONDITION_ANY_SPECIFIC.ANY,
    message: CONDITION_MESSAGE.ANY,
    timeframe: CONDITION_TIMEFRAME.LAST,
    sometimeLast: 7,
    sometimeBetween: {
        from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        to: new Date(),
    },
    timeUnit: CONDITION_TIME_UNIT.DAY,
    priceType: CONDITION_AMOUNT_TYPE.ANY,
    price: 100,
    priceTotal: CONDITION_PRICE_TOTAL.TOTAL,
}

const timeframeOptions = (condition: ConditionsProps) => {
    const nodeKey = Object.values(condition).join(',');
    return {
        nodeKey,
        isLineBreaker: true,
        placeholder: 'Time selection',
        key: 'timeframe',
        label: 'Timeframe: ',
        initialSelectedKey: 'sometimeLast',
        options: [{
                key: CONDITION_TIMEFRAME.ANY,
                label: 'Anytime',
            }, {
                key: CONDITION_TIMEFRAME.BETWEEN,
                label: 'Between',
                next : {
                    key: 'sometimeBetween',
                    input: 'date',
                }
            }, {
                key: CONDITION_TIMEFRAME.LAST,
                label: 'in the last ',
                next : {
                    key: 'sometimeLast',
                    input: 'number',
                    initialSelectedVal: 7,
                    next: {
                        key: 'timeUnit',
                        initialSelectedKey: 'days',
                        options: [{
                            key: CONDITION_TIME_UNIT.DAY,
                            label: 'Days',
                        }, {
                            key: CONDITION_TIME_UNIT.WEEK,
                            label: 'Weeks',
                        }, {
                            key: CONDITION_TIME_UNIT.MONTH,
                            label: 'Months',
                        }, {
                            key: CONDITION_TIME_UNIT.YEAR,
                            label: 'Years',
                        }]
                    }
                }
            }
        ]
    }
}

const priceTotalOptions = (condition: ConditionsProps) => {
    const next = (priceTotal: CONDITION_PRICE_TOTAL) => timeframeOptions({ ...condition, priceTotal });
    const nodeKey = Object.values(condition).join(',');
    const totalOption = { key: CONDITION_PRICE_TOTAL.TOTAL, label: 'In total', next: next(CONDITION_PRICE_TOTAL.TOTAL) };
    const eachOption = { key: CONDITION_PRICE_TOTAL.EACH, label: 'Each', next: next(CONDITION_PRICE_TOTAL.EACH) };

    const options = [totalOption];
    if(condition.action !== CONDITION_ACTION.SPEND) {
        options.push(eachOption);
    }

    return {
        key: 'priceTotal',
        nodeKey,
        initialSelectedKey: CONDITION_PRICE_TOTAL.TOTAL,
        options,
    }
}

const priceTypeOptions = (condition: ConditionsProps) => {
    const next = (priceType: CONDITION_AMOUNT_TYPE) => {
        if(priceType === CONDITION_AMOUNT_TYPE.ANY) {
            return timeframeOptions({ ...condition, priceType: CONDITION_AMOUNT_TYPE.ANY });
        } else {
            return { key: 'price', input: 'number', initialSelectedVal: 0, next: priceTotalOptions({...condition, priceType}), closerLabel: '$', singleCloserLabel: '$' };
        }
    };
    const anyOption = { key: 'any', label: 'In any price', next: next(CONDITION_AMOUNT_TYPE.ANY) };
    const exactlyOption = { key: 'exactly', label: 'Exactly', next: next(CONDITION_AMOUNT_TYPE.EXACTLY) };
    const overOption = { key: 'over', label: 'Over', next: next(CONDITION_AMOUNT_TYPE.OVER) };
    const belowOption = { key: 'below', label: 'Below', next: next(CONDITION_AMOUNT_TYPE.BELOW) };
    const nodeKey = Object.values(condition).join(',');

    return {
        label: 'Price: ',
        isLineBreaker: true,
        key: 'priceType',
        nodeKey,
        initialSelectedKey: CONDITION_AMOUNT_TYPE.ANY,
        options: [anyOption, exactlyOption, overOption, belowOption],
    }
}

const amountTypeOptions = (condition: ConditionsProps) => {
    const nextPrice = (amountType: CONDITION_AMOUNT_TYPE) => priceTypeOptions({ ...condition, amountType });
    const nextTime = (amountType: CONDITION_AMOUNT_TYPE) => timeframeOptions({ ...condition, amountType });
    const next = condition.action &&
        (
            ['see', 'click', 'ignore', 'locatedAt'].includes(condition.action) ||
            (['abandon', 'purchase'].includes(condition.action) && condition.product === 'specific')
        )
        ? nextTime : nextPrice;
    const anyOption = { key: CONDITION_AMOUNT_TYPE.ANY, label: 'In any amount', next: next(CONDITION_AMOUNT_TYPE.ANY) };
    const exactlyOption = { key: CONDITION_AMOUNT_TYPE.EXACTLY, label: 'Exactly', next: { key: 'amount', input: 'number', initialSelectedVal: 7, next: next(CONDITION_AMOUNT_TYPE.EXACTLY), closerLabel: 'times', singleCloserLabel: 'time' }};
    const overOption = { key: CONDITION_AMOUNT_TYPE.OVER, label: 'Over', next: { key: 'amount', input: 'number', initialSelectedVal: 7, next: next(CONDITION_AMOUNT_TYPE.OVER), closerLabel: 'times', singleCloserLabel: 'time' }};
    const belowOption = { key: CONDITION_AMOUNT_TYPE.BELOW, label: 'Below', next: { key: 'amount', input: 'number', initialSelectedVal: 7, next: next(CONDITION_AMOUNT_TYPE.BELOW), closerLabel: 'times', singleCloserLabel: 'time' }};
    const nodeKey = Object.values(condition).join(',');

    return {
        label: 'Quantity: ',
        isLineBreaker: true,
        key: 'amountType',
        nodeKey,
        initialSelectedKey: CONDITION_AMOUNT_TYPE.ANY,
        options: [anyOption, exactlyOption, overOption, belowOption],
    }
}

const countryOptions = (condition: ConditionsProps) => {
    const nodeKey = Object.values(condition).join(',');
    return {
        key: 'country',
        nodeKey,
        options: [{
            key: 'any',
            label: 'Anywhere'
        }],
        list: 'countryList',
        initialSelectedKey: 'any',
    }
}

const productOptions = (condition: ConditionsProps) => {
    const nodeKey = Object.values(condition).join(',');
    return {
        key: 'product',
        nodeKey,
        options: [{
            key: CONDITION_ANY_SPECIFIC.ANY,
            label: 'Any products',
            next: amountTypeOptions({ ...condition, product: CONDITION_ANY_SPECIFIC.ANY }),
        }],
        list: 'productList',
        initialSelectedKey: 'any',
        next: amountTypeOptions({ ...condition, product: CONDITION_ANY_SPECIFIC.SPECIFIC }),
    }
}

const messageOptions = (condition: ConditionsProps) => {
    const nodeKey = Object.values(condition).join(',');
    return {
        key: 'message',
        nodeKey,
        options: [{
            key: CONDITION_MESSAGE.ANY,
            label: 'Any message',
            next: amountTypeOptions({ ...condition, message: CONDITION_MESSAGE.ANY }),
        }, {
            key: CONDITION_MESSAGE.RECOVERY,
            label: 'Recovery message',
            next: amountTypeOptions({ ...condition, message: CONDITION_MESSAGE.RECOVERY }),
        }, {
            key: CONDITION_MESSAGE.EVENT,
            label: 'Event message',
            next: amountTypeOptions({ ...condition, message: CONDITION_MESSAGE.EVENT }),
        }, {
            key: CONDITION_MESSAGE.CAMPAIGN,
            label: 'Campaign message',
            next: campaignOptions({ ...condition, message: CONDITION_MESSAGE.CAMPAIGN }),
        }]
    }
}

const campaignOptions = (condition: ConditionsProps) => {
    const nodeKey = Object.values(condition).join(',');
    return {
        isLineBreaker: true,
        label: 'Campaign: ',
        key: 'campaign',
        nodeKey,
        initialSelectedKey: CONDITION_ANY_SPECIFIC.ANY,
        options: [{
            key: CONDITION_ANY_SPECIFIC.ANY,
            label: 'Any campaign',
            next: amountTypeOptions({ ...condition, campaign: CONDITION_ANY_SPECIFIC.ANY })
        }],
        list: 'campaignList',
        next: amountTypeOptions({ ...condition, campaign: CONDITION_ANY_SPECIFIC.SPECIFIC })
    }
}

const actions = (condition: ConditionsProps) => {
    const did = condition.behavior === CONDITION_BEHAVIOR.DID;
    const nodeKey = Object.values(condition).join(',');
    return {
        key: 'action',
        nodeKey,
        placeholder: 'Select Action',
        options: [...(did ? [{
            key: CONDITION_ACTION.JOIN,
            label: 'Join',
            next: timeframeOptions({ ...condition, action: CONDITION_ACTION.JOIN }),
        }] : []), {
            key: CONDITION_ACTION.SPEND,
            label: 'Spend',
            next: priceTypeOptions({ ...condition, action: CONDITION_ACTION.SPEND }),
        }, {
            key: CONDITION_ACTION.ABANDON,
            label: 'Abandon',
            next: productOptions({ ...condition, action: CONDITION_ACTION.ABANDON }),
        }, {
            key: CONDITION_ACTION.PURCHASE,
            label: 'Purchase',
            next: productOptions({ ...condition, action: CONDITION_ACTION.PURCHASE }),
        }, {
            key: CONDITION_ACTION.SEE,
            label: 'See',
            next: messageOptions({ ...condition, action: CONDITION_ACTION.SEE }),
        }, {
            key: CONDITION_ACTION.CLICK,
            label: 'Click',
            next: messageOptions({ ...condition, action: CONDITION_ACTION.CLICK }),
        }, {
            key: CONDITION_ACTION.CONVERT_FROM,
            label: 'Convert from',
            next: messageOptions({ ...condition, action: CONDITION_ACTION.CONVERT_FROM }),
        }, {
            key: CONDITION_ACTION.IGNORE,
            label: 'Ignore',
            next: messageOptions({ ...condition, action: CONDITION_ACTION.IGNORE }),
        }, {
            key: CONDITION_ACTION.LOCATED_AT,
            label: 'Log in from',
            next: countryOptions({ ...condition, action: CONDITION_ACTION.LOCATED_AT }),
        }]
    };
}


export const segmentBuilderJourney: any = {
    key: 'behavior',
    placeholder: 'Select Behavior',
    label: 'Customers who',
    isLabelPartOfSentence: true,
    initialSelectedKey: CONDITION_BEHAVIOR.DID,
    options: [{
            key: CONDITION_BEHAVIOR.DID,
            label: 'Did',
            next: actions({ behavior: CONDITION_BEHAVIOR.DID }),
        }, {
            key: CONDITION_BEHAVIOR.DIDNOT,
            label: 'Did not',
            next: actions({ behavior: CONDITION_BEHAVIOR.DIDNOT }),
        }
    ],
}